import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, RefreshCw, Inbox, CheckCircle2, AlertTriangle,
  Clock, Copy, Filter, ExternalLink,
} from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* ────────────────────────────────────────────────────────────────────────
   Bank Reconciliation Dashboard
   Aggregated view: imported / matched / unmatched / pending / duplicates.
   Counters + filters + drill-down list. Pure read-only — actions live on
   the existing /banking/reconciliation page.
   ──────────────────────────────────────────────────────────────────────── */

const BUCKETS = [
  { key: "imported",   label: "Imported",   Icon: Inbox,         accent: "blue"    },
  { key: "matched",    label: "Matched",    Icon: CheckCircle2,  accent: "emerald" },
  { key: "unmatched",  label: "Unmatched",  Icon: AlertTriangle, accent: "amber"   },
  { key: "pending",    label: "Pending",    Icon: Clock,         accent: "gray"    },
  { key: "duplicates", label: "Duplicates", Icon: Copy,          accent: "rose"    },
];

const accentClass = {
  blue:    "border-blue-200    bg-blue-50    text-blue-800    hover:border-blue-400    data-[active=true]:bg-blue-100",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400 data-[active=true]:bg-emerald-100",
  amber:   "border-amber-200   bg-amber-50   text-amber-800   hover:border-amber-400   data-[active=true]:bg-amber-100",
  gray:    "border-gray-200    bg-white       text-gray-800   hover:border-gray-400    data-[active=true]:bg-gray-100",
  rose:    "border-rose-200    bg-rose-50    text-rose-800    hover:border-rose-400    data-[active=true]:bg-rose-100",
};

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const activeBucket = params.get("bucket") || "imported";
  const filters = useMemo(() => ({
    ledger_id:  params.get("ledger_id")  || "",
    start:      params.get("start")      || "",
    end:        params.get("end")        || "",
    min_amount: params.get("min_amount") || "",
    max_amount: params.get("max_amount") || "",
  }), [params]);

  const load = useCallback(async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const { data: d } = await api.get(
        `/companies/${activeId}/bank/recon-dashboard?${qs.toString()}`,
      );
      setData(d);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setBusy(false);
    }
  }, [activeId, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/ledgers`).then((r) => {
      setLedgers((r.data || []).filter((L) =>
        L.group === "Bank Accounts" || L.group === "Cash-in-Hand"));
    });
  }, [activeId]);

  const setBucket = (k) => {
    const next = new URLSearchParams(params); next.set("bucket", k); setParams(next);
  };
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const clearFilters = () => setParams(new URLSearchParams({ bucket: activeBucket }));

  const bucketRows = data?.buckets?.[activeBucket] || [];

  return (
    <>
      <PageHeader
        title="Reconciliation Dashboard"
        subtitle="Imported · Matched · Unmatched · Pending · Duplicates — at a glance"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={busy} className="h-8 text-xs" data-testid="btn-refresh-dashboard">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="ghost" onClick={() => nav("/banking")} className="h-8 text-sm" data-testid="btn-back-banking">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Banking
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-4">
        {/* Counter tiles — clicking switches the bucket below */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dashboard-tiles">
          {BUCKETS.map(({ key, label, Icon, accent }) => (
            <button
              key={key}
              data-active={activeBucket === key}
              onClick={() => setBucket(key)}
              data-testid={`tile-${key}`}
              className={`text-left border rounded-md px-3 py-3 transition ${accentClass[accent]}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="w-4 h-4 opacity-80" />
                <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
              </div>
              <div className="text-2xl font-semibold mt-1" data-testid={`count-${key}`}>
                {data ? (data.stats[key] ?? 0) : "—"}
              </div>
            </button>
          ))}
        </div>

        {/* Filter strip */}
        <div className="border border-gray-200 rounded bg-white p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Filter className="w-3.5 h-3.5" /> Filters:
          </span>
          <select
            value={filters.ledger_id}
            onChange={(e) => setFilter("ledger_id", e.target.value)}
            className="h-8 rounded border border-gray-300 px-2 text-xs"
            data-testid="filter-ledger"
          >
            <option value="">All bank ledgers</option>
            {ledgers.map((L) => <option key={L.id} value={L.id}>{L.name}</option>)}
          </select>
          <Input
            type="date" value={filters.start}
            onChange={(e) => setFilter("start", e.target.value)}
            className="h-8 w-36 text-xs"
            data-testid="filter-start"
            placeholder="Start"
          />
          <Input
            type="date" value={filters.end}
            onChange={(e) => setFilter("end", e.target.value)}
            className="h-8 w-36 text-xs"
            data-testid="filter-end"
            placeholder="End"
          />
          <Input
            type="number" value={filters.min_amount}
            onChange={(e) => setFilter("min_amount", e.target.value)}
            className="h-8 w-28 text-xs"
            data-testid="filter-min"
            placeholder="Min ₹"
          />
          <Input
            type="number" value={filters.max_amount}
            onChange={(e) => setFilter("max_amount", e.target.value)}
            className="h-8 w-28 text-xs"
            data-testid="filter-max"
            placeholder="Max ₹"
          />
          {(filters.ledger_id || filters.start || filters.end || filters.min_amount || filters.max_amount) && (
            <Button variant="ghost" onClick={clearFilters} className="h-7 text-xs" data-testid="btn-clear-filters">
              Clear
            </Button>
          )}
          <span className="ml-auto text-[11px] text-gray-500">
            Showing <strong className="text-gray-800">{bucketRows.length}</strong> {activeBucket} transaction{bucketRows.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Bucket transactions */}
        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-600 font-semibold flex items-center justify-between">
            <span>{BUCKETS.find((b) => b.key === activeBucket)?.label} transactions</span>
            {activeBucket === "duplicates" && (
              <span className="text-[10px] normal-case tracking-normal text-amber-700">
                Same date + amount + narration pattern
              </span>
            )}
          </div>
          <div className="overflow-x-auto max-h-[64vh] overflow-y-auto">
            <table className="tx-table text-xs">
              <thead className="sticky top-0 bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)] z-10">
                <tr>
                  <th className="w-24">Date</th>
                  <th className="w-24">No.</th>
                  <th className="w-24">Type</th>
                  <th>Narration</th>
                  <th className="text-right w-28">Amount</th>
                  <th className="w-20">Source</th>
                  <th className="w-20">Status</th>
                </tr>
              </thead>
              <tbody>
                {bucketRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400">No transactions in this bucket.</td></tr>
                )}
                {bucketRows.map((r) => (
                  <tr key={`${r.id}`} data-testid={`row-recon-${r.id}`}>
                    <td className="font-mono text-[11px]">{r.date}</td>
                    <td className="font-mono text-[11px]">{r.voucher_no || "—"}</td>
                    <td className="text-[11px]">{r.voucher_type}</td>
                    <td>
                      <div className="line-clamp-2 break-words" title={r.narration}>{r.narration || "—"}</div>
                    </td>
                    <td className="tx-num">{formatINR(r.total)}</td>
                    <td>
                      {r.imported ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200">
                          AI Import
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-500">Manual</span>
                      )}
                    </td>
                    <td>
                      {r.matched ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                          Matched
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {data && (
          <div className="text-[11px] text-gray-500 flex items-center gap-2">
            <ExternalLink className="w-3 h-3" />
            Need to mark transactions matched? Use the{" "}
            <a href="/banking/reconciliation" className="text-blue-700 hover:underline">Reconciliation</a> page.
          </div>
        )}
      </div>
    </>
  );
}

export default function BankReconDashboard() {
  return <RequireCompany><Inner /></RequireCompany>;
}
